import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import CustomModal from './CustomModal';
import { getData } from '../../utils/httpClient';
import ClientsEditModal from './ClientsEditModal';

const columnHelper = createColumnHelper();

export default function ClientsTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  handleUpdateCustomer,
  onDeleteCustomer,
  sorting,
  onSortingChange,
}) {
  const [modalState, setModalState] = React.useState({
    isOpen: false,
    data: null,
  });
  const [clientVehicles, setClientVehicles] = React.useState([]);
  const [clientServices, setClientServices] = React.useState([]);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData],
  );

  const [editModalState, setEditModalState] = React.useState({ isOpen: false });
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  const getClientVehicles = async (clientId) => {
    try {
      const response = await getData(`/vehicles/customer/${clientId}`);
      if (response?.payload?.vehicles) {
        setClientVehicles(response.payload.vehicles);
      }
    } catch (error) {
      toast({
        title: 'Error al obtener vehículos del cliente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getClientServicesHistory = async (clientId) => {
    try {
      const response = await getData(`/vehicleServices/customer/${clientId}`);
      if (response?.payload?.vehicleServices) {
        setClientServices(response.payload.vehicleServices);
      }
    } catch (error) {
      toast({
        title: 'Error al obtener servicios del cliente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openModal = (actionType, rowData) => {
    setModalState({ isOpen: true, data: { actionType, rowData } });
    getClientVehicles(rowData.id);
    getClientServicesHistory(rowData.id);
  };

  const closeModal = () => {
    setModalState({ isOpen: false, data: null });
    setClientVehicles([]);
  };

  const openEditModal = (rowData) => {
    setSelectedCustomer(rowData);
    setEditModalState({ isOpen: true });
  };

  const closeEditModal = () => {
    setEditModalState({ isOpen: false });
    setSelectedCustomer(null);
  };

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => (
          <Flex align="center">
            <Text fontSize="sm" fontWeight="normal">
              {col.cell ? col.cell(info) : info.getValue()}
            </Text>
            {col.actions &&
              col.actions.map((action, index) => (
                <IconButton
                  key={index}
                  aria-label={action.label}
                  icon={action.icon}
                  size="sm"
                  variant="ghost"
                  colorScheme={action.colorScheme}
                  ml={2}
                  onClick={() => {
                    if (action.type === 'edit') {
                      openEditModal(info.row.original);
                    } else if (action.type === 'view') {
                      openModal(action.type, info.row.original);
                    } else if (action.type === 'delete' && onDeleteCustomer) {
                      onDeleteCustomer(info.row.original);
                    }
                  }}
                />
              ))}
          </Flex>
        ),
      }),
    );
  }

  return (
    <>
      <PageTable
        flexDirection="column"
        w="100%"
        px="0px"
       >
        <Box>
          <PageTable.Scroll>
            <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                        <Thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <SortableTableHeader
                                  key={header.id}
                                  header={header}
                                  borderColor={borderColor}
                                />
                              ))}
                            </Tr>
                          ))}
                        </Thead>
                        <Tbody>
                          {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
          </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </Box>

        <CustomModal
          modalState={modalState}
          closeModal={closeModal}
          clientVehicles={clientVehicles}
          clientServices={clientServices}
        />
      </PageTable>
      <ClientsEditModal
        isOpen={editModalState.isOpen}
        onClose={closeEditModal}
        customer={selectedCustomer}
        onUpdate={handleUpdateCustomer}
      />
    </>
  );
}
